import { faQuestionCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { CourseEnrolment } from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { MultiSelect } from 'src/web/components/Input/Dropdowns/MultiSelect/component';
import { ModalDialog } from 'src/web/components/Modals/ModalDialog';
import { OL_COLOUR_NAVY_BABY } from 'src/web/components/Style/colours';
import { Tooltip } from 'src/web/Tooltip/Tooltip';
import { useGetInstitutionCohortsByCoursePathApi } from '../../../hooks';
import {
  StyledTimesButton,
  CourseDetailItem,
  CourseName,
  CoursePath,
  TotalSelectedMembers,
  ErrorMessage,
} from '../../../styles';
import { AssignedCourseRolesType } from '../../../types';
import {
  CoursesListTable,
  StyledSingleSelect,
  ClassesInRoleCell,
  StaffRoleOptionsTable,
  CourseItemTr,
  CourseItemTd,
  CourseItemTh,
} from './styles';

type ClassRolesStateType = {
  [cohortPath: string]: {
    facilitator: boolean;
    assessor: boolean;
    // eslint-disable-next-line camelcase
    assessment_designer: boolean;
  };
};

const ClassRolesOptions = ({
  institutionPath,
  coursePath,
  onChange,
}: {
  institutionPath: string;
  coursePath: string;
  onChange: (param: ClassRolesStateType) => void;
}): JSX.Element => {
  const [selectedClass, setSelectedClass] = useState<
    Readonly<{ label: string; value: string }[]>
  >([]);
  const [classRolesState, setClassRolesState] = useState<ClassRolesStateType>(
    {}
  );

  const { cohortsData, isLoading } = useGetInstitutionCohortsByCoursePathApi(
    institutionPath,
    coursePath
  );

  useEffect(() => {
    const newClassRolesState: ClassRolesStateType = {};
    const existingCohortPaths = Object.keys(classRolesState);
    selectedClass.forEach(({ value: cohortPath }) => {
      if (existingCohortPaths.includes(cohortPath)) {
        // if existing in classRolesState, persist the state
        newClassRolesState[cohortPath] = classRolesState[cohortPath];
      } else {
        // if not existing in classRolesState, initiate with default value
        newClassRolesState[cohortPath] = {
          facilitator: false,
          assessor: false,
          assessment_designer: false,
        };
      }
    });
    setClassRolesState(newClassRolesState);
  }, [selectedClass]);

  useEffect(() => {
    onChange(classRolesState);
  }, [classRolesState]);

  const changeCohortRoles = (
    cohortPath: string,
    roleType: 'facilitator' | 'assessor' | 'assessment_designer',
    value: boolean
  ): void => {
    const updatedCohortRoles = {
      [cohortPath]: {
        ...classRolesState[cohortPath],
        [roleType]: value,
      },
    };
    setClassRolesState((prev) => ({
      ...prev,
      ...updatedCohortRoles,
    }));
  };

  const classOptions = useMemo(
    () =>
      cohortsData
        ? cohortsData.courses[0]?.classes.map((cohort) => ({
            label: cohort.name,
            value: cohort.path,
          }))
        : [],
    [cohortsData]
  );

  const removeSelectedClass = (removedValue: string): void => {
    setSelectedClass((prev) =>
      prev.filter(({ value }) => value !== removedValue)
    );
  };

  return (
    <div>
      <MultiSelect
        placeholder="Add a class"
        isLoading={isLoading}
        options={classOptions}
        value={selectedClass}
        setValues={(values) => setSelectedClass(values)}
      />
      <StaffRoleOptionsTable>
        {selectedClass.length > 0 && (
          <tr>
            <th>Class permissions</th>
            <th>
              F{' '}
              <Tooltip placement="top" overlay="Facilitates">
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  color={OL_COLOUR_NAVY_BABY}
                />
              </Tooltip>
            </th>
            <th>
              A{' '}
              <Tooltip placement="top" overlay="Assesses">
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  color={OL_COLOUR_NAVY_BABY}
                />
              </Tooltip>
            </th>
            <th>
              D{' '}
              <Tooltip placement="top" overlay="Designs Assessment">
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  color={OL_COLOUR_NAVY_BABY}
                />
              </Tooltip>
            </th>
            <th />
          </tr>
        )}
        {selectedClass.map(({ label, value: cohortPath }) => (
          <tr key={cohortPath}>
            <td>{label}</td>
            <td>
              <input
                type="checkbox"
                name="is-facilitator"
                checked={classRolesState[cohortPath]?.facilitator}
                onChange={() => {
                  changeCohortRoles(
                    cohortPath,
                    'facilitator',
                    !classRolesState[cohortPath]?.facilitator
                  );
                }}
              />
            </td>
            <td>
              <input
                type="checkbox"
                name="is-assessor"
                checked={classRolesState[cohortPath]?.assessor}
                onChange={() => {
                  changeCohortRoles(
                    cohortPath,
                    'assessor',
                    !classRolesState[cohortPath]?.assessor
                  );
                }}
              />
            </td>
            <td>
              <input
                type="checkbox"
                name="is-assessment-designer"
                checked={classRolesState[cohortPath]?.assessment_designer}
                onChange={() => {
                  changeCohortRoles(
                    cohortPath,
                    'assessment_designer',
                    !classRolesState[cohortPath]?.assessment_designer
                  );
                }}
              />
            </td>
            <td>
              <StyledTimesButton
                type="button"
                onClick={() => removeSelectedClass(cohortPath)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </StyledTimesButton>
            </td>
          </tr>
        ))}
      </StaffRoleOptionsTable>
    </div>
  );
};

export interface AssignToCoursesModalProps {
  isAssigningFromCoursesLoading: boolean;
  totalSelectedMembers: number;
  errorMessage: string;
  courses: CourseEnrolment[];
  institutionPath: string;
  onAssignToCourses: (param: AssignedCourseRolesType) => void;
  onHide: () => void;
}

const CLASS_ROLES = [
  {
    label: 'Administrator',
    value: 'admin',
  },
  {
    label: 'Staff',
    value: 'staff',
  },
];

export const AssignToCoursesModal: React.FC<AssignToCoursesModalProps> = ({
  isAssigningFromCoursesLoading,
  totalSelectedMembers,
  errorMessage,
  institutionPath,
  courses,
  onHide,
  onAssignToCourses,
}) => {
  const [selectedCourses, setSelectedCourses] = useState<
    Readonly<{ label: string; value: string }[]>
  >([]);

  const [userInput, setUserInput] = useState<AssignedCourseRolesType>({});

  useEffect(() => {
    const newUserInput: typeof userInput = {};
    const existingCoursesPaths = Object.keys(userInput);
    selectedCourses.forEach((course) => {
      if (existingCoursesPaths.includes(course.value)) {
        // if existing in the userInput state, persist the state
        newUserInput[course.value] = userInput[course.value];
      } else {
        // if not existing in the userInput state, initiate with default value
        newUserInput[course.value] = {
          role: 'admin',
        };
      }
    });
    setUserInput({
      ...newUserInput,
    });
  }, [selectedCourses]);

  const removeSelectedCourses = (removedValue: string): void => {
    setSelectedCourses((prev) =>
      prev.filter(({ value }) => value !== removedValue)
    );
  };

  const updateCourseRoleOption = (
    coursePath: string,
    role: 'staff' | 'admin'
  ): void => {
    setUserInput((prev) => ({
      ...prev,
      [coursePath]: role === 'admin' ? { role } : { role, cohortRoles: {} },
    }));
  };

  // Should only be called when user course role equals to staff
  const updateCohortRolesOption = (
    coursePath: string,
    cohortRoles: ClassRolesStateType
  ): void => {
    setUserInput((prev) => ({
      ...prev,
      [coursePath]: {
        role: 'staff',
        cohortRoles,
      },
    }));
  };

  const coursesLookupMap = useMemo(() => {
    const coursesMapByPath: { [path: string]: CourseEnrolment } = {};
    courses.forEach((course) => {
      coursesMapByPath[course.path] = course;
    });
    return coursesMapByPath;
  }, [courses]);

  const coursesOptions = useMemo(
    () =>
      courses.map((course) => ({
        label: course.name,
        value: course.path,
      })),
    [courses]
  );

  return (
    <ModalDialog
      dialogClassName="assign-to-courses"
      show
      center
      small
      onHide={onHide}
      title="Assign to courses"
      actions={[
        {
          label: 'Assign to courses',
          showSpinner: isAssigningFromCoursesLoading,
          disabled: isAssigningFromCoursesLoading,
          onClick: () => onAssignToCourses(userInput),
          type: 'primary-kelp',
        },
      ]}
    >
      <TotalSelectedMembers>
        {totalSelectedMembers} member{totalSelectedMembers > 1 ? 's' : ''}
      </TotalSelectedMembers>
      <MultiSelect
        label="Select courses to assign team members to"
        placeholder="Add a course"
        options={coursesOptions}
        value={selectedCourses}
        setValues={(values) => setSelectedCourses(values)}
      />
      {selectedCourses.length > 0 && (
        <CoursesListTable>
          <CourseItemTr>
            <CourseItemTh>Course name/URL</CourseItemTh>
            <CourseItemTh>Course role</CourseItemTh>
            <CourseItemTh>Classes in role</CourseItemTh>
            <CourseItemTh />
          </CourseItemTr>
          {selectedCourses.map(({ label, value: coursePath }) => (
            <CourseItemTr key={coursePath}>
              <CourseItemTd>
                <CourseDetailItem>
                  <img
                    src={coursesLookupMap[coursePath].promoImage}
                    alt="course-promoImage"
                  />
                  <div>
                    <CourseName>{label}</CourseName>
                    <CoursePath>{coursePath}</CoursePath>
                  </div>
                </CourseDetailItem>
              </CourseItemTd>

              <CourseItemTd>
                <StyledSingleSelect
                  options={CLASS_ROLES}
                  value={CLASS_ROLES.find(
                    (role) => role.value === userInput[coursePath]?.role
                  )}
                  onValueChange={(selectedValue) =>
                    updateCourseRoleOption(
                      coursePath,
                      selectedValue?.value as 'staff' | 'admin'
                    )
                  }
                />
              </CourseItemTd>
              <CourseItemTd>
                <ClassesInRoleCell>
                  {userInput[coursePath]?.role === 'admin' ? (
                    <span>
                      Administrators can can access all classes, manage
                      permissions of others in the course, and edit course
                      content.
                    </span>
                  ) : userInput[coursePath]?.role === 'staff' ? (
                    <ClassRolesOptions
                      institutionPath={institutionPath}
                      coursePath={coursePath}
                      onChange={(newCohortRoles) =>
                        updateCohortRolesOption(coursePath, newCohortRoles)
                      }
                    />
                  ) : (
                    <></>
                  )}
                </ClassesInRoleCell>
              </CourseItemTd>
              <CourseItemTd>
                <StyledTimesButton
                  type="button"
                  onClick={() => removeSelectedCourses(coursePath)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </StyledTimesButton>
              </CourseItemTd>
            </CourseItemTr>
          ))}
        </CoursesListTable>
      )}
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </ModalDialog>
  );
};
